<template>
  <div>
    <canvas ref="generic_chart" width="160" height="90" />
  </div>
</template>

<script>
import _ from 'lodash'
import 'velocity-ui-pack'
import Chart from 'chart.js'
import 'chartjs-plugin-datalabels'
import 'chartjs-plugin-streaming'
import chartColors from './chart_colors.js'
import onecolor from 'onecolor'

// *****************************************************************************

let color = Chart.helpers.color

function current_chart_colors(graph_type, nb_classes) {
  const colors_list1 = [
    chartColors.purple,
    chartColors.orange,
    chartColors.green,
  ]
  const colors_list2 = [chartColors.red, chartColors.grey]
  const colors_list3 = [chartColors.blue, chartColors.red]
  const colors_list4 = colors_list1.concat(colors_list3)
  const colors_per_graph = {
    bar: colors_list1,
    polarArea: colors_list1,
    doughnut: colors_list1,
    radar: colors_list1,
    bubble: colors_list1,
    line: colors_list2,
    horizontalBar: colors_list4,
    mixed: colors_list3,
  }
  const colors = colors_per_graph[graph_type]
  const nb_colors = colors.length
  let res
  if (nb_classes <= nb_colors) {
    res = colors.slice(0, nb_classes)
  } else {
    res = []
    let q = Math.floor(nb_classes / nb_colors)
    let rem = nb_classes % nb_colors
    for (let i = 0; i < q; ++i) {
      res = res.concat(colors)
    }
    res = res.concat(colors.slice(0, rem))
  }
  return res
}

class GenericChart {
  constructor(el, context, title) {
    this.el = el
    this.context = _.cloneDeep(context)
    this.title = title
  }
  init() {
    if (this.context.type === 'line') {
      let colors = current_chart_colors(
        this.context.type,
        this.context.classes.length
      )
      this.real_chart = new Chart(this.el, {
        type: this.context.type,
        data: {
          labels: this.context.get_classes_label(),
          datasets: [
            {
              label: 'Dataset (line)',
              borderColor: color(colors[0]).rgbString(),
              backgroundColor: color(colors[0]).rgbString(),
              fill: false,
              data: this.context.get_classes_values('val'),
            },
            {
              label: 'Min',
              borderColor: color(colors[1]).rgbString(),
              backgroundColor: color(colors[1]).rgbString(),
              fill: false,
              fillOpacity: 1,
              data: this.context.get_classes_values('min'),
            },
            {
              label: 'Max',
              borderColor: color(colors[1]).rgbString(),
              backgroundColor: onecolor(colors[1])
                .alpha(0.4)
                .cssa(),
              fill: '-1',
              data: this.context.get_classes_values('max'),
            },
          ],
        },

        options: {
          responsive: true,
          hover: {
            mode: 'nearest',
            intersect: false,
          },
          title: {
            display: true,
            text: this.title,
          },
        },
      })
    } else if (this.context.type === 'mixed') {
      let colors = current_chart_colors(
        this.context.type,
        this.context.classes.length
      )
      let width_percentages
      let datasets_init
      if (this.context.is_quantitative) {
        width_percentages = [1.0, 1.0]
        datasets_init = [
          {
            label: 'Densité de la variable',
            data: this.context.get_classes_values(),
            borderColor: colors[1],
            backgroundColor: colors[1],
            fill: false,

            type: 'line',
            datalabels: {
              display: false,
            },
          },
          {
            label: "Proportion d'observations par catégorie",
            data: this.context.get_classes_values(),
            backgroundColor: colors[0],
          },
        ]
      } else {
        width_percentages = [0.5, 1.0]
        datasets_init = [
          {
            label: "Proportion d'observations par catégorie",
            data: this.context.get_classes_values(),
            backgroundColor: colors[0],
          },
        ]
      }
      this.real_chart = new Chart(this.el, {
        type: 'bar',
        data: {
          labels: this.context.get_classes_label(),
          datasets: datasets_init,
        },
        options: {
          responsive: true,
          hover: {
            mode: 'nearest',
            intersect: false,
          },
          title: {
            display: true,
            text: this.title,
          },
          scales: {
            xAxes: [
              {
                display: this.context.xdisplay,
                stacked: true,
                barPercentage: width_percentages[0],
                categoryPercentage: width_percentages[1],
              },
            ],
            yAxes: [
              {
                display: this.context.ydisplay,
                ticks: {
                  suggestedMin: 0,
                },
              },
            ],
          },
          plugins: {
            streaming: false,
            datalabels: {
              display: true,
              color: '#ffffff',
              font: {
                family: "'Bitter', serif",
                weight: 'bold',
                size: 18,
              },
              formatter(value, context) {
                return value
              },
            },
          },
        },
      })
    } else {
      let colors = current_chart_colors(
        this.context.type,
        this.context.classes.length
      )
      this.real_chart = new Chart(this.el, {
        type: this.context.type,
        data: {
          labels: this.context.get_classes_label(),
          datasets: [
            {
              label: 'Dataset 1',
              backgroundColor: current_chart_colors(
                this.context.type,
                this.context.classes.length
              ),
              data: this.context.get_classes_values(),
            },
          ],
        },

        options: {
          responsive: true,
          maintainAspectRatio: true,
          title: {
            display: true,
            text: this.title,
          },
          plugins: {
            datalabels: {
              display: true,
              color: '#ffffff',
              font: {
                family: "'Bitter', serif",
                weight: 'bold',
                size: 18,
              },
              formatter: function(value, context) {
                return value.toPrecision(2)
              },
            },
            streaming: false,
          },
          legend: { display: false },
          scales: {
            xAxes: [
              {
                barPercentage: 1,
                categoryPercentage: 0.6,
                display: this.context.xdisplay,
                stacked: true,
                gridLines: {
                  display: true,
                  drawBorder: false,
                },
              },
            ],
            yAxes: [
              {
                display: this.context.ydisplay,
                ticks: {
                  fontSize: 15,
                },
                stacked: true,
                barPercentage: 0.8,
                categoryPercentage: 0.8,
                gridLines: {
                  display: true,
                  drawBorder: true,
                },
              },
            ],
          },
        },
      })
    }
  }
  update(context) {
    this.context = _.cloneDeep(context)
    this.real_chart.data.labels = this.context.get_classes_label()
    if (this.context.type === 'line') {
      let colors = current_chart_colors(
        this.context.type,
        this.context.classes.length
      )
      this.real_chart.data.datasets[0].borderColor = color(
        colors[0]
      ).rgbString()
      this.real_chart.data.datasets[0].backgroundColor = color(
        colors[0]
      ).rgbString()
      this.real_chart.data.datasets[0].data = this.context.get_classes_values(
        'val'
      )

      this.real_chart.data.datasets[1].borderColor = color(
        colors[1]
      ).rgbString()
      this.real_chart.data.datasets[1].backgroundColor = color(
        colors[1]
      ).rgbString()
      this.real_chart.data.datasets[1].data = this.context.get_classes_values(
        'min'
      )

      this.real_chart.data.datasets[2].borderColor = color(
        colors[1]
      ).rgbString()
      this.real_chart.data.datasets[2].backgroundColor = onecolor(colors[1])
        .alpha(0.4)
        .cssa()
      this.real_chart.data.datasets[2].data = this.context.get_classes_values(
        'max'
      )
    } else {
      this.real_chart.data.datasets[0].backgroundColor = current_chart_colors(
        this.context.type,
        this.context.classes.length
      )
      this.real_chart.data.datasets[0].data = this.context.get_classes_values()
    }
    this.real_chart.update()
  }
  uninit() {
    this.real_chart.destroy()
  }
}

// *****************************************************************************

export default {
  name: 'Chart',
  props: ['initial_chart_context'],
  data() {
    return {
      chart_object: undefined,
      title: undefined,
    }
  },
  watch: {
    initial_chart_context: {
      handler(new_value) {
        if (new_value !== undefined) {
          this.title = undefined
          if (new_value.title !== '') {
            this.title = new_value.title
          }
          if (
            this.chart_object !== undefined &&
            this.chart_object.context.type === new_value.type
          ) {
            this.chart_object.update(new_value)
          } else {
            if (this.chart_object !== undefined) {
              this.chart_object.uninit()
              delete this.chart_object
            }
            this.chart_object = new GenericChart(new_value, this.title)
            this.chart_object.init()
          }
        }
      },
      deep: true,
    },
  },

  mounted() {
    if (!_.isUndefined(this.initial_chart_context)) {
      this.title = undefined
      if (this.initial_chart_context.title !== '') {
        this.title = this.initial_chart_context.title
      }
      if (
        !_.isUndefined(this.chart_object) &&
        this.chart_object.context.type === this.initial_chart_context.type
      ) {
        this.chart_object.update(this.initial_chart_context)
      } else {
        if (!_.isUndefined(this.chart_object)) {
          this.chart_object.uninit()
          delete this.chart_object
        }
        this.chart_object = new GenericChart(
          this.$refs.generic_chart,
          this.initial_chart_context,
          this.title
        )
        this.chart_object.init()
      }
    }
  },

  beforeDestroy() {
    if (!_.isUndefined(this.chart_object)) {
      this.chart_object.uninit()
      delete this.chart_object
    }
  },
}
</script>
